
import React from 'react';
import OnboardingLayout from '../../OnboardingLayout';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Clock, ThumbsUp } from 'lucide-react';
import { mockArticles } from '@/components/reader-home/mockData';

interface FirstArticleProps {
  formData: any;
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onPrevious: () => void;
}

const FirstArticle: React.FC<FirstArticleProps> = ({
  formData,
  currentStep,
  totalSteps,
  onNext,
  onPrevious
}) => {
  // Filter articles based on selected interests
  const getRecommendedArticles = () => {
    if (!formData.interests || formData.interests.length === 0) {
      return mockArticles.slice(0, 3);
    }
    
    return mockArticles
      .filter(article => 
        article.topics.some((topic: string) => 
          formData.interests.includes(topic)
        )
      )
      .slice(0, 3);
  };
  
  const recommendedArticles = getRecommendedArticles();

  return (
    <OnboardingLayout
      title="Start Reading"
      subtitle="Here are some articles we think you'll enjoy"
      currentStep={currentStep}
      totalSteps={totalSteps}
      onNext={onNext}
      onPrevious={onPrevious}
    >
      <div className="max-w-4xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {recommendedArticles.map((article) => (
            <Card key={article.id} className="overflow-hidden flex flex-col">
              <div className="h-48 overflow-hidden">
                <img 
                  src={article.image} 
                  alt={article.title} 
                  className="w-full h-full object-cover"
                />
              </div>
              
              <CardContent className="pt-4 flex-1">
                <div className="flex flex-wrap gap-2 mb-2">
                  {article.topics.slice(0, 2).map((topic) => (
                    <Badge key={topic} variant="outline" className="bg-muted/50">
                      {topic}
                    </Badge>
                  ))}
                </div>
                
                <h3 className="font-bold text-lg line-clamp-2 mb-2">{article.title}</h3>
                
                <div className="flex items-center text-sm text-muted-foreground mb-2">
                  <span>By {article.author}</span>
                </div>
                
                <p className="text-muted-foreground text-sm line-clamp-3 mb-2">
                  {article.excerpt}
                </p>
                
                <div className="flex items-center justify-between text-xs text-muted-foreground mt-auto">
                  <div className="flex items-center">
                    <Clock className="h-3 w-3 mr-1" />
                    <span>{article.readTime}</span>
                  </div>
                  
                  <div className="flex items-center">
                    <ThumbsUp className="h-3 w-3 mr-1" />
                    <span>{Math.floor(Math.random() * 100) + 20}</span>
                  </div>
                </div>
              </CardContent>
              
              <CardFooter className="pt-0 pb-4">
                <Button variant="outline" size="sm" className="w-full">
                  Read Article
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
        
        <div className="text-center mb-6">
          <h3 className="text-lg font-medium mb-2">Ready to explore more?</h3>
          <p className="text-muted-foreground mb-4">
            After completing onboarding, you'll have access to the full collection of articles
            from top journalists across all your interests.
          </p>
          <Button onClick={onNext}>Continue to next step</Button>
        </div>
        
        <div className="bg-muted/30 rounded-lg p-4">
          <h4 className="text-sm font-medium mb-2">Reading tips:</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• Bookmark articles to read later</li>
            <li>• Use the AI assistant to get summaries</li>
            <li>• Follow journalists to get notified of new articles</li>
            <li>• Join the discussion in the comments section</li>
          </ul>
        </div>
      </div>
    </OnboardingLayout>
  );
};

export default FirstArticle;
